import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiJson } from '@/lib/apiClient';
import type { TrackerHealth, VehicleState } from '@/features/live/types';
import { normalizeTrackerHealth } from '@/features/live/trackerHealth';
import { normalizeVehicleState } from '@/features/live/vehicleState';

/**
 * Client-side view of `GET /api/vehicles` (slice 06, PLAN §"The contract").
 *
 * Freshness is split into two distinct signals, because they answer two
 * different questions and conflating them is the defect this slice fixes:
 *
 *   - `positionFixedAt` / `positionAgeSec` — Traccar's `fixTime`: WHERE the
 *     vehicle was last located. This is the ONLY customer-facing freshness value
 *     ("where is my vehicle"). The age is computed server-side against the
 *     server's clock; the UI renders that number rather than recomputing from
 *     the timestamp, so a wrong phone clock can't drift it.
 *   - `trackerContactAt` — Traccar's `lastUpdate`: when the tracker last made
 *     contact. A diagnostic signal only. It must NEVER be presented as location
 *     freshness. On the pilot's device the two are ~1h35m apart.
 *
 * `freshnessDiverged` is the server's verdict that the two differ by more than
 * the divergence threshold. It is carried on the wire but no longer drives a
 * separate note: the freshness line now states the contact-vs-position case
 * directly (`freshness.ts`), which subsumes and improves on it.
 *
 * `status` is `'unknown'` and the freshness fields are null/false for every
 * vehicle when the liveness source is down; `liveness.degraded` is the signal.
 * The list of vehicles itself always comes from our own DB, so an outage costs
 * the liveness fields only — never the list.
 */

export type VehicleStatus = 'online' | 'offline' | 'unknown';
export type VehiclePlan = 'track' | 'fleet' | 'pro';

export interface VehicleListItem {
  id: string;
  plate: string;
  label: string | null;
  model: string | null;
  plan: VehiclePlan;
  status: VehicleStatus;
  /** Seam S1 (13e) — the vehicle-state vocabulary, on the list item too, so
   *  /vehicles can state what the vehicle is doing in one line without a detail
   *  page. */
  state: VehicleState;
  /** 15e seam — server-derived: was the last stored fix at/above the moving
   *  threshold? Chooses `engineStale`'s sub-line. Null ⇒ no sub-line. */
  lastFixMoving: boolean | null;
  /** Seam S1 (13e) — tracker health, on the list item too, so /vehicles can render
   *  the "No satellite view" tell and battery beside the state. `null` ⇒ no
   *  telemetry; an all-null object ⇒ reporting with no readings. */
  trackerHealth: TrackerHealth | null;
  /** ISO-8601 fixTime — when the vehicle was last LOCATED. Null when unknown. */
  positionFixedAt: string | null;
  /** Server-computed seconds since the fix, clamped >= 0. Null when unknown. */
  positionAgeSec: number | null;
  /** ISO-8601 lastUpdate — tracker contact recency. Diagnostic only. */
  trackerContactAt: string | null;
  /** True when position and tracker-contact freshness differ beyond threshold. */
  freshnessDiverged: boolean;
  /**
   * 18g — is a tracker FITTED to this vehicle right now?
   *
   * 🔴 AN ADDITIVE BOOLEAN, AND IT IS DELIBERATELY NOT A NEW `VehicleState` OR
   * `Motion` MEMBER. Both of those enums are persisted through localStorage (the
   * live seed) and read by exhaustive `Record<VehicleState, …>` maps; widening
   * either would change the meaning of bytes already sitting in customers'
   * browsers, in ways nothing here can bound. A boolean beside them changes
   * nothing that is already stored.
   *
   * 🔴 IT IS IDENTITY, NOT LIVENESS. `false` means the tracker is out of the
   * vehicle — away for repair, or moved — and the API enforces on the wire that
   * it implies no position and no live data at all. `true` with no position is
   * the ordinary state of a tracker that has not reported yet, which is a
   * completely different sentence and must never render as "No tracker".
   *
   * A vehicle with no tracker KEEPS its trips, its history and its detail page.
   * That is the entire point of the maintenance case.
   */
  hasTracker: boolean;
  /**
   * 13f seam S5 — ISO start of the vehicle's OPEN `tracker_offline` alert, or
   * `null` when the tracker is reporting. Drives the "Tracker silent since hh:mm"
   * honest-state sub-line on the list; the server derives it, the UI renders it.
   */
  trackerSilentSince: string | null;
}

export interface VehicleListResponse {
  vehicles: VehicleListItem[];
  liveness: { degraded: boolean };
  /** ISO-8601 server clock at response time — the reference for any client age. */
  serverTime: string;
}

export const vehiclesQueryKey = ['vehicles'] as const;

export function useVehicles(): UseQueryResult<VehicleListResponse> {
  return useQuery({
    queryKey: vehiclesQueryKey,
    queryFn: async ({ signal }) => {
      const res = await apiJson<VehicleListResponse>('/api/vehicles', { signal });
      // Normalise the two seam-S1 fields at the boundary (13e): an absent/unknown
      // `state` becomes `unknown` (the honest default), and `trackerHealth`
      // preserves the null-vs-present distinction. Done here so every surface can
      // treat both as required and never guard for the wire not carrying them yet.
      return {
        ...res,
        // DEFENSIVE, and it earned this: the browser gate caught `res.vehicles.map`
        // throwing on a body without a `vehicles` array. Before 13e the consumers
        // guarded (`data?.vehicles ?? []`); moving normalisation to the boundary
        // moved the assumption with it, so an unexpected shape stopped being an
        // empty list and became a thrown TypeError inside the query function —
        // which takes the whole page down, including screens that only borrow this
        // query for a plate lookup (Alerts). Normalising must not be able to fail.
        vehicles: (Array.isArray(res?.vehicles) ? res.vehicles : []).map((v): VehicleListItem => ({
          ...v,
          state: normalizeVehicleState((v as { state?: unknown }).state),
          trackerHealth: normalizeTrackerHealth((v as { trackerHealth?: unknown }).trackerHealth),
          // 18g. 🔴 ABSENT MEANS `true`, NEVER `false`, AND THE DIRECTION IS THE
          // WHOLE DECISION. `hasTracker === false` paints a "No tracker" chip; if
          // an older or degraded response omitted the field and we defaulted to
          // `false`, EVERY vehicle in the fleet would be labelled as having no
          // tracker — a confident, specific, fleet-wide lie. Defaulting the other
          // way can only ever fail to show a chip on one vehicle that deserves it,
          // which the rest of the row still describes honestly.
          hasTracker: (v as { hasTracker?: unknown }).hasTracker !== false,
          // 13f seam S5 — pass through, defaulting an absent field to null (no
          // open offline episode) rather than undefined, so every consumer can
          // treat it as `string | null` and never guard for the wire not carrying
          // it yet.
          trackerSilentSince:
            (v as { trackerSilentSince?: string | null }).trackerSilentSince ?? null,
        })),
      };
    },
  });
}
